import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import downArrow from "./../../resources/down_arrow.png";

import { ReactComponent as HomeIcon } from "./../../resources/icons/home.svg";
import { ReactComponent as UserIcon } from "./../../resources/icons/user.svg";
import { ReactComponent as AssignorIcon } from "./../../resources/icons/assignor.svg";
import { ReactComponent as HelmetIcon } from "./../../resources/icons/helmet.svg";
import { ReactComponent as StatsIcon } from "./../../resources/icons/stats.svg";
import { ReactComponent as DotsIcon } from "../../resources/icons/dots.svg";
import Logo from "./../../resources/icons/logo.png";
import { User } from "../../types";

interface Props {
  user: User;
}

const AdminSidebar = ({ user }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const submenuPrefixes = {
    management: "/admin/management",
    assignors: "/admin/assignors",
    contractors: "/admin/contractors",
    statistics: "/admin/statistics",
  };

  const [activeRoute, setActiveRoute] = useState("");
  const [isHomeOpen, setIsHomeOpen] = useState(true);
  const lastSectionRef = useRef<string | null>(null);

  useEffect(() => {
    const path = location.pathname;

    if (
      path.includes("/admin/assignors") ||
      path.includes("/admin/assignor/")
    ) {
      setActiveRoute(submenuPrefixes.assignors);
      lastSectionRef.current = submenuPrefixes.assignors;
      sessionStorage.setItem("lastSection", submenuPrefixes.assignors);
    } else if (
      path.includes("/admin/contractors") ||
      path.includes("/admin/contractor/")
    ) {
      setActiveRoute(submenuPrefixes.contractors);
      lastSectionRef.current = submenuPrefixes.contractors;
      sessionStorage.setItem("lastSection", submenuPrefixes.contractors);
    } else if (path.includes("/admin/statistics")) {
      setActiveRoute(submenuPrefixes.statistics);
      lastSectionRef.current = submenuPrefixes.statistics;
      sessionStorage.setItem("lastSection", submenuPrefixes.statistics);
    } else if (path.includes("/admin/management")) {
      setActiveRoute(submenuPrefixes.management);
      lastSectionRef.current = submenuPrefixes.management;
      sessionStorage.setItem("lastSection", submenuPrefixes.management);
    } else if (path.includes("/projects/")) {
      const remembered = lastSectionRef.current || sessionStorage.getItem("lastSection");
      if (remembered) {
        setActiveRoute(remembered);
        lastSectionRef.current = remembered;
      } else {
        setActiveRoute("");
      }
    } else if (path.includes('/my-profile')) {
      setActiveRoute("my-profile");
    } else {
      setActiveRoute("");
    }
  }, [location.pathname]);
  
  

  const toggleHome = (open?: boolean) => {
    setIsHomeOpen(open === undefined ? !isHomeOpen : open);
    setActiveRoute("");
    navigate("/admin");
  };

  const handleNavigation = (route: string) => {
    setActiveRoute(route);
    navigate(route);
  };

  return (
    <aside className="fixed left-0 top-0 w-[17.5rem] h-screen bg-[#202F3D] flex flex-col border-r border-[#D0D5DD] z-50">
      <button className="h-[4.5rem] p-6 flex items-center gap-2 border-b border-[#D0D5DD] text-white" onClick={() => toggleHome(true)}>
        <span>
          <img src={Logo} alt="Logo" />
        </span>
        <h2 className="text-xl font-bold">Build Connect</h2>
      </button>

      <nav className="flex-1 overflow-y-auto px-[1.5rem] pt-[1rem] text-white">
        <div className="mb-4">
          <button
            onClick={() => toggleHome()}
            className={`flex items-center cursor-pointer py-1 px-2 rounded transition-colors w-full ${
              isHomeOpen && activeRoute === "" ? "bg-[#364450]" : ""
            }`}
          >
            <HomeIcon className="w-5 h-5 mr-2" />
            <span className="font-medium">Home</span>
            <img
              src={downArrow}
              alt="Toggle dropdown"
              className={`w-5 h-5 ml-auto transform transition-transform filter brightness-0 invert ${
                isHomeOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {isHomeOpen && (
            <div className="mt-2 flex flex-col gap-2">
              <button
                onClick={() => handleNavigation(submenuPrefixes.management)}
                className={`flex items-center px-6 py-1 rounded transition-colors ${
                  activeRoute === submenuPrefixes.management
                    ? "bg-[#364450]"
                    : ""
                }`}
              >
                <UserIcon className="w-5 h-5 mr-2" />
                User management
              </button>
              <button
                onClick={() => handleNavigation(submenuPrefixes.assignors)}
                className={`flex items-center px-6 py-1 rounded transition-colors ${
                  activeRoute === submenuPrefixes.assignors
                    ? "bg-[#364450]"
                    : ""
                }`}
              >
                <AssignorIcon className="w-5 h-5 mr-2" />
                Assignors
              </button>
              <button
                onClick={() => handleNavigation(submenuPrefixes.contractors)}
                className={`flex items-center px-6 py-1 rounded transition-colors ${
                  activeRoute === submenuPrefixes.contractors
                    ? "bg-[#364450]"
                    : ""
                }`}
              >
                <HelmetIcon className="w-5 h-5 mr-2" />
                Contractors
              </button>
              <button
                onClick={() => handleNavigation(submenuPrefixes.statistics)}
                className={`flex items-center px-6 py-1 rounded transition-colors ${
                  activeRoute === submenuPrefixes.statistics
                    ? "bg-[#364450]"
                    : ""
                }`}
              >
                <StatsIcon className="w-5 h-5 mr-2" />
                Statistics
              </button>
            </div>
          )}
        </div>
      </nav>

      <footer
        className={`m-4 pt-3 hover:bg-[#364450] hover:rounded-md hover:cursor-pointer text-white ${location.pathname.includes('my-profile') ? 'bg-[#364450] rounded-md' : 'border-t border-[#364450] rounded-none'}`}
        onClick={() => navigate("/my-profile")}
      >
        <div className="p-2 flex flex-col items-start">
          <div className="flex w-full items-center justify-between">
            <span className="font-bold text-base">{user.name}</span>
            <DotsIcon
              className="w-5 h-5 cursor-pointer"
            />
          </div>
          <span className="text-sm text-gray-300">{user.email}</span>
        </div>
      </footer>
    </aside>
  );
};

export default AdminSidebar;
